import { CartUpdatedEvent, ThemeEvents } from '@theme/events';
import { EpicPackBase } from '@theme/epic-pack-core';

/**
 * Epic Box Configurator
 *
 * Configurateur de page produit : N configurations indépendantes de l'Epic Box.
 * Chaque composant (Top / Token / Skin) est rendu en colonne avec image + select ;
 * les sources sont 3 fiches produit dédiées (réglées dans le bloc, pas de metafield).
 *
 * Tout le calcul de prix (packs, supplément, Tower shiny, Loot seule, prix barré) vit
 * dans EpicPackBase (assets/epic-pack-core.js). Ici on fournit seulement :
 *   - le chargement des données + le rendu (colonnes, options, couleur Tower) ;
 *   - les 4 hooks (isPremium / baseVariant / towerVariant / optionState) ;
 *   - l'ajout panier (multi-lignes selon la quantité).
 *
 * Une « selection » = une config de this.configs (multi-config = 1 par unité).
 *
 * Données injectées par blocks/_epic-configurator.liquid dans
 * <script type="application/json" data-ref="epic-config-data">.
 */
class EpicConfigurator extends EpicPackBase {
  constructor() {
    super();

    this.components = [];
    /** base = Epic Box Sur Mesure, tower = Epic Pack, full = Full Pack. */
    this.packs = { base: { variants: [] }, tower: { variants: [] }, full: { variants: [] } };
    this.options = { tower: { enabled: false, variants: [] }, lootbox: { enabled: false } };
    this.initialOptions = { tower: false, lootbox: false };
    /** @type {Map<string, object>} */
    this.variantIndex = new Map();
    this.quantity = 1;
    /** @type {Array<Record<string, string>>} */
    this.configs = [];
    this.chevron = '';

    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onAddToCart = this.onAddToCart.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-ref="epic-config-data"]');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Epic Configurator: données JSON invalides', e);
      return;
    }

    this.components = data.components || [];
    this.chevron = this.querySelector('[data-ref="epic-chevron"]')?.innerHTML || '';

    if (this.components.length === 0) return;

    // Lookup variantId -> composant (propriétés panier + prix)
    this.components.forEach((component) => {
      component.variants.forEach((variant) => {
        this.variantIndex.set(String(variant.id), {
          componentKey: component.key,
          title: variant.title,
          image: variant.image,
          label: component.label,
          available: variant.available,
          price: variant.price,
        });
      });
    });

    if (data.packs) this.packs = data.packs;
    if (data.options) this.options = data.options;

    // État initial des options depuis l'URL : ?tower=1&lootbox=1
    const params = new URLSearchParams(window.location.search);
    this.initialOptions = {
      tower: params.get('tower') === '1',
      lootbox: params.get('lootbox') === '1',
    };
    if (this.options.lootbox?.requiresTower && !this.initialOptions.tower) {
      this.initialOptions.lootbox = false;
    }

    // Prix de base de chaque composant = variante la moins chère (relief = au-dessus)
    this.componentBasePrice = {};
    this.components.forEach((component) => {
      const prices = component.variants
        .map((v) => Number(v.price))
        .filter((n) => !Number.isNaN(n));
      this.componentBasePrice[component.key] = prices.length ? Math.min(...prices) : 0;
    });

    this.configsContainer = this.querySelector('[data-ref="epic-configs"]');
    this.qtyInput = this.querySelector('[data-ref="epic-qty-input"]');
    this.addButton = this.querySelector('[data-ref="epic-add-to-cart"]');
    this.totalEl = this.querySelector('[data-ref="epic-total"]');

    this.quantity = Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
    this.syncConfigsToQuantity();
    this.renderConfigs();
    this.updateTotal();

    this.qtyInput?.addEventListener('change', this.onQuantityChange);
    this.addButton?.addEventListener('click', this.onAddToCart);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    this.qtyInput?.removeEventListener('change', this.onQuantityChange);
    this.addButton?.removeEventListener('click', this.onAddToCart);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** Retour arrière (bfcache / form-restoration) : relit les selects puis recalcule. */
  onPageShow() {
    this.syncFromDom();
  }

  syncFromDom() {
    if (!this.configsContainer) return;
    Array.from(this.configsContainer.children).forEach((card, index) => {
      const config = this.configs[index];
      if (!config) return;
      card.querySelectorAll('.epic-col').forEach((col) => {
        const key = col.dataset.componentKey;
        const select = col.querySelector('select');
        const img = col.querySelector('[data-ref="epic-col-image"]');
        if (!select) return;
        config[key] = select.value;
        const option = select.options[select.selectedIndex];
        const vimg = option?.dataset.variantImage;
        if (vimg && img) {
          img.src = vimg;
          img.classList.remove('epic-col__image--empty');
        }
      });
      const towerSelect = card.querySelector(`#epic-${index}-tower-color`);
      if (towerSelect && towerSelect.value) config.__tower = towerSelect.value;
      this.refreshOptionsRow(index);
    });
    this.updateTotal();
  }

  // ── Hooks du moteur de prix (EpicPackBase) ───────────────────────────────────

  /** Relief : un composant choisi dépasse son prix de base (ex. top relief 20€ > 15€). */
  isPremium(config) {
    return this.components.some((c) => {
      const info = this.variantIndex.get(String(config[c.key]));
      const surcharge = Number(info?.price) - (this.componentBasePrice[c.key] || 0);
      return surcharge > 0;
    });
  }

  /** Box seule : variante Standard/Premium choisie par prix selon le relief. */
  baseVariant(config) {
    return this.pickByPrice(this.packs.base?.variants || [], this.isPremium(config));
  }

  towerVariant(config) {
    const variants = this.options.tower?.variants || [];
    if (!variants.length) return null;
    return variants.find((v) => String(v.id) === String(config.__tower)) || variants[0];
  }

  optionState(config) {
    const o = config.__options || {};
    return { tower: Boolean(o.tower), lootbox: Boolean(o.lootbox) };
  }

  // ── État ───────────────────────────────────────────────────────────────────

  defaultConfig() {
    const config = {};
    this.components.forEach((component) => {
      const firstAvailable =
        component.variants.find((v) => v.available) || component.variants[0];
      config[component.key] = firstAvailable ? String(firstAvailable.id) : '';
    });
    config.__options = { ...this.initialOptions };
    const towerVariants = this.options.tower?.variants || [];
    config.__tower = towerVariants.length ? String(towerVariants[0].id) : '';
    return config;
  }

  syncConfigsToQuantity() {
    while (this.configs.length < this.quantity) this.configs.push(this.defaultConfig());
    if (this.configs.length > this.quantity) this.configs.length = this.quantity;
  }

  onQuantityChange() {
    this.quantity = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    this.syncConfigsToQuantity();
    this.renderConfigs();
    this.updateTotal();
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────

  renderConfigs() {
    this.configsContainer.innerHTML = '';
    this.configs.forEach((config, index) => {
      this.configsContainer.appendChild(this.buildConfigCard(config, index));
    });
  }

  buildConfigCard(config, index) {
    const card = document.createElement('div');
    card.className = 'epic-config';
    card.dataset.index = String(index);

    const header = document.createElement('div');
    header.className = 'epic-config__header';
    const title = document.createElement('p');
    title.className = 'epic-config__title variant_label';
    const baseLabel = this.dataset.configLabel || 'Epic Box';
    title.textContent = index > 0 ? `${baseLabel} N°${index + 1}` : baseLabel;
    header.appendChild(title);
    card.appendChild(header);

    const columns = document.createElement('div');
    columns.className = 'epic-config__columns';
    this.components.forEach((component) => {
      columns.appendChild(this.buildColumn(component, config, index));
    });
    card.appendChild(columns);

    const optionsRow = this.buildOptionsRow(config, index);
    if (optionsRow) card.appendChild(optionsRow);

    return card;
  }

  // ── Options (packs) ──────────────────────────────────────────────────────────

  buildOptionsRow(config, index) {
    const tower = this.options.tower;
    const loot = this.options.lootbox;
    const towerOn = tower?.enabled && this.packs.tower?.variants?.length > 0;
    const lootOn = loot?.enabled && this.packs.full?.variants?.length > 0;
    if (!towerOn && !lootOn) return null;

    const wrap = document.createElement('div');
    wrap.className = 'epic-options';

    if (this.dataset.optionsTitle) {
      const title = document.createElement('p');
      title.className = 'epic-options__title variant_label';
      title.textContent = this.dataset.optionsTitle;
      wrap.appendChild(title);
    }

    if (towerOn) {
      wrap.appendChild(this.buildOptionToggle('tower', tower, config, index));
      if (config.__options?.tower) {
        const colorRow = this.buildTowerColorRow(config, index);
        if (colorRow) wrap.appendChild(colorRow);
      }
    }
    if (lootOn) wrap.appendChild(this.buildOptionToggle('lootbox', loot, config, index));
    return wrap;
  }

  buildOptionToggle(key, opt, config, index) {
    const row = document.createElement('label');
    row.className = 'epic-option';
    row.dataset.optionKey = key;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'epic-option__checkbox';
    checkbox.checked = Boolean(config.__options?.[key]);

    if (key === 'lootbox' && opt.requiresTower && !config.__options?.tower) {
      checkbox.checked = false;
      checkbox.disabled = true;
      row.classList.add('epic-option--disabled');
    }

    checkbox.addEventListener('change', () =>
      this.onOptionChange(index, key, checkbox.checked),
    );
    row.appendChild(checkbox);

    if (opt.image) {
      const img = document.createElement('img');
      img.className = 'epic-option__image';
      img.src = opt.image;
      img.alt = opt.label || key;
      img.width = 40;
      img.height = 40;
      img.loading = 'lazy';
      row.appendChild(img);
    }

    const label = document.createElement('span');
    label.className = 'epic-option__label';
    label.textContent = opt.label || key;
    row.appendChild(label);

    const tag = this.buildPriceTag(this.optionPricing(config, key));
    if (tag) row.appendChild(tag);

    return row;
  }

  buildTowerColorRow(config, index) {
    const variants = this.options.tower?.variants || [];
    if (variants.length < 2) return null;

    const wrap = document.createElement('div');
    wrap.className = 'epic-tower-color';

    const label = document.createElement('span');
    label.className = 'epic-tower-color__label';
    label.textContent = this.dataset.towerColorLabel || 'Couleur de la Tower';
    wrap.appendChild(label);

    const selectWrap = document.createElement('div');
    selectWrap.className = 'select-wrapper';
    const select = document.createElement('select');
    select.className = 'select';
    select.id = `epic-${index}-tower-color`;
    select.autocomplete = 'off';
    select.setAttribute('aria-label', label.textContent);
    variants.forEach((v) => {
      const option = document.createElement('option');
      option.value = String(v.id);
      option.textContent = v.title;
      if (String(v.id) === String(config.__tower)) option.selected = true;
      select.appendChild(option);
    });
    if (config.__tower) select.value = String(config.__tower);
    select.addEventListener('change', (e) =>
      this.onTowerColorChange(index, e.currentTarget.value),
    );
    selectWrap.appendChild(select);

    const iconWrap = document.createElement('div');
    iconWrap.className = 'select-wrapper__icon';
    iconWrap.innerHTML = this.chevron;
    selectWrap.appendChild(iconWrap);

    wrap.appendChild(selectWrap);
    return wrap;
  }

  onTowerColorChange(index, variantId) {
    this.configs[index].__tower = String(variantId);
    this.refreshOptionsRow(index);
    this.updateTotal();
  }

  onOptionChange(index, key, checked) {
    const config = this.configs[index];
    if (!config.__options) config.__options = {};
    config.__options[key] = checked;
    if (key === 'tower' && !checked && this.options.lootbox?.requiresTower) {
      config.__options.lootbox = false;
    }
    this.refreshOptionsRow(index);
    this.updateTotal();
  }

  refreshOptionsRow(index) {
    const card = this.configsContainer.children[index];
    if (!card) return;
    const oldRow = card.querySelector('.epic-options');
    const newRow = this.buildOptionsRow(this.configs[index], index);
    if (oldRow && newRow) oldRow.replaceWith(newRow);
    else if (oldRow && !newRow) oldRow.remove();
  }

  buildColumn(component, config, index) {
    const selectedId = config[component.key];
    const selectedVariant =
      component.variants.find((v) => String(v.id) === String(selectedId)) ||
      component.variants[0];

    const col = document.createElement('div');
    col.className = 'epic-col card card--with-border';
    col.dataset.componentKey = component.key;

    const imageWrap = document.createElement('div');
    imageWrap.className = 'epic-col__image';
    const img = document.createElement('img');
    img.dataset.ref = 'epic-col-image';
    img.width = 300;
    img.height = 300;
    img.loading = 'lazy';
    img.alt = component.label;
    if (selectedVariant && selectedVariant.image) {
      img.src = selectedVariant.image;
    } else {
      img.classList.add('epic-col__image--empty');
    }
    imageWrap.appendChild(img);
    col.appendChild(imageWrap);

    const labelRow = document.createElement('div');
    labelRow.className = 'epic-col__label-row';
    const label = document.createElement('span');
    label.className = 'epic-col__label variant_label';
    label.textContent = component.label;
    labelRow.appendChild(label);
    const count = document.createElement('span');
    count.className = 'epic-col__count';
    count.textContent = `${component.variants.length} choix`;
    labelRow.appendChild(count);
    col.appendChild(labelRow);

    const selectWrap = document.createElement('div');
    selectWrap.className = 'select-wrapper';
    const select = document.createElement('select');
    select.className = 'select';
    select.id = `epic-${index}-${component.key}`;
    select.name = `epic-${index}-${component.key}`;
    select.autocomplete = 'off';
    select.setAttribute('aria-label', component.label);
    component.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.title;
      if (variant.image) option.dataset.variantImage = variant.image;
      if (variant.price != null) option.dataset.price = String(variant.price);
      if (!variant.available) {
        option.dataset.unavailable = 'true';
        option.textContent = `${variant.title} (épuisé)`;
      }
      if (String(variant.id) === String(selectedId)) option.selected = true;
      select.appendChild(option);
    });
    if (selectedId) select.value = String(selectedId);
    select.addEventListener('change', (e) =>
      this.onSelectChange(col, component.key, e.currentTarget, img),
    );
    selectWrap.appendChild(select);

    const iconWrap = document.createElement('div');
    iconWrap.className = 'select-wrapper__icon';
    iconWrap.innerHTML = this.chevron;
    selectWrap.appendChild(iconWrap);

    col.appendChild(selectWrap);
    return col;
  }

  onSelectChange(col, componentKey, select, img) {
    const card = col.closest('.epic-config');
    const index = parseInt(card.dataset.index, 10);
    const option = select.options[select.selectedIndex];

    this.configs[index][componentKey] = option.value;

    const variantImage = option.dataset.variantImage;
    if (variantImage) {
      img.src = variantImage;
      img.classList.remove('epic-col__image--empty');
    }

    this.refreshOptionsRow(index);
    this.updateTotal();
  }

  /** Total = somme des configs (prix barré plein + promo). */
  updateTotal() {
    if (!this.totalEl) return;
    let promo = 0;
    let full = 0;
    this.configs.forEach((config) => {
      promo += this.unitTotalPrice(config);
      full += this.unitFullPrice(config);
    });
    this.renderTotal(promo, full);
  }

  // ── Panier ─────────────────────────────────────────────────────────────────

  async onAddToCart() {
    if (!this.packs.base?.variants?.length || this.configs.length === 0) return;

    this.addButton.classList.add('is-loading');
    this.addButton.disabled = true;

    const items = [];
    this.configs.forEach((config, i) => {
      const boxUid = `${Date.now().toString(36)}-${i}`;

      const boxProperties = { _box: boxUid };
      this.components.forEach((component) => {
        const info = this.variantIndex.get(String(config[component.key]));
        boxProperties[component.label] = info ? info.title : '';
      });

      const o = config.__options || {};
      if (o.tower) {
        const tv = this.towerVariant(config);
        boxProperties[this.options.tower?.label || 'Epic Tower'] = tv ? tv.title : '';
      }
      if (o.tower && o.lootbox) {
        boxProperties[this.options.lootbox?.label || 'Loot Box'] =
          this.options.lootbox?.versionLabel || 'V2';
      }

      const variant = this.resolveMainVariant(config);
      if (!variant) return;
      items.push({ id: variant.id, quantity: 1, properties: boxProperties });

      // Loot Box SEULE (sans Tower) : ligne séparée au plein tarif.
      if (o.lootbox && !o.tower && this.options.lootbox?.addVariantId) {
        items.push({
          id: this.options.lootbox.addVariantId,
          quantity: 1,
          properties: { _box: boxUid },
        });
      }
    });

    if (items.length === 0) {
      this.addButton.classList.remove('is-loading');
      this.addButton.disabled = false;
      return;
    }

    const sections = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer) sections.push(drawer.dataset.sectionId);

    const cartAddUrl =
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Ajout au panier impossible');
      }

      const json = await response.json();
      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            source: 'epic-configurator',
            itemCount: json.item_count,
            sections: json.sections,
          },
        ),
      );
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartOpen));
    } catch (error) {
      console.error('Epic Configurator: ajout panier échoué', error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: error.message },
        }),
      );
    } finally {
      this.addButton.classList.remove('is-loading');
      this.addButton.disabled = false;
    }
  }
}

if (!customElements.get('epic-configurator')) {
  customElements.define('epic-configurator', EpicConfigurator);
}
